import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded"
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import _ from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "react-hot-toast"
import { ReactComponent as NoScheme } from "../../assets/No_offers_ui.svg"
import useAuth from "../../hooks/useAuth"
import ROUTE_PATHS from "../../routes/routesPath"
import IfEmpty from "../../shared/constants/IfEmpty"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import { Loader } from "../../shared/components/UI"
import {
  createSchemeSubscription,
  getSchemeMasters,
  getSchemeStoreOptions,
} from "../../service/scheme"
import { SchemeContainer } from "../../styles/styledComponent"
import AmountPresetGrid from "./AmountPresetGrid"
import { AMOUNT_PRESETS } from "./constants"
import { getSchemeError } from "./errors"
import { formatAmount, toAmount } from "./format"
import { getAmountError, getMaturityValue } from "./selectors"
import { canJoin, getInitialStoreId, toStoreLabel } from "./storeOptions"
import schemeTokens from "./tokens"
import useSchemeBusiness from "./useSchemeBusiness"

const SummaryRow = ({ label, value, highlight, bold }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    spacing={1}
    sx={{ py: 0.75 }}
  >
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: bold ? 700 : 400,
        color: bold ? "text.primary" : "text.secondary",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: bold ? 15 : 13,
        fontWeight: 700,
        color: highlight ? "primary.main" : "text.primary",
      }}
    >
      {value}
    </Typography>
  </Stack>
)

const JoinScheme = () => {
  const navigate = useNavigate()
  const { token } = useAuth()
  const business = useSchemeBusiness()
  const businessRef = useRef(business)
  businessRef.current = business

  const [masters, setMasters] = useState([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [amount, setAmount] = useState(null)
  const [custom, setCustom] = useState(false)
  const [customAmount, setCustomAmount] = useState("")
  const [agreed, setAgreed] = useState(false)
  const [stores, setStores] = useState([])
  const [storeId, setStoreId] = useState(null)
  const [storesFailed, setStoresFailed] = useState(false)

  const master = masters[0]

  useEffect(() => {
    if (!token) return
    setLoading(true)
    setAmount(null)
    setCustom(false)
    setCustomAmount("")
    setStoreId(null)
    setStores([])
    getSchemeMasters({ token, business })
      .then((data) => setMasters(_.isArray(data) ? data : []))
      .catch(() => setMasters([]))
      .finally(() => setLoading(false))
  }, [token, business])

  const loadStores = useCallback(() => {
    if (!token) return
    setStoresFailed(false)
    getSchemeStoreOptions({ token, business })
      .then((options) => {
        if (businessRef.current !== business) return
        setStores(_.isArray(options?.stores) ? options.stores : [])
        setStoreId((current) => current ?? getInitialStoreId(options))
      })
      .catch(() => {
        if (businessRef.current !== business) return
        setStoresFailed(true)
        toast.error(STRING_CONSTANTS.STORE_OPTIONS_LOAD_FAILED)
      })
  }, [token, business])

  useEffect(() => {
    loadStores()
  }, [loadStores])

  useEffect(() => {
    if (master && amount === null) {
      const minAmount = toAmount(master.minAmount)
      const preset =
        AMOUNT_PRESETS.find((value) => value >= minAmount) || minAmount
      setAmount(preset)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [master])

  const selectedAmount = custom ? toAmount(customAmount) : toAmount(amount)
  const amountError = useMemo(
    () => (master ? getAmountError(selectedAmount, master) : null),
    [selectedAmount, master]
  )

  const customerInstalments = master?.customerInstallments || 11
  const bonusInstalments = master?.bonusInstallments || 1
  const youPay = selectedAmount * customerInstalments
  const contribution = selectedAmount * bonusInstalments
  const totalWalletValue = youPay + contribution
  const planName = master?.name || STRING_CONSTANTS.SCHEME

  const onSubmit = () => {
    if (!master || !canJoin({ agreed, amountError, submitting, storeId }))
      return
    setSubmitting(true)
    createSchemeSubscription({
      token,
      business,
      schemeMasterId: master.id,
      monthlyAmount: selectedAmount,
      tenantStoreId: storeId,
    })
      .then((scheme) => {
        toast.success(STRING_CONSTANTS.SCHEME_JOINED)
        navigate(`${ROUTE_PATHS.SCHEME}/${scheme.id}`)
      })
      .catch((error) => {
        toast.error(getSchemeError(error))
        setSubmitting(false)
      })
  }

  if (loading) return <Loader />

  return (
    <SchemeContainer>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton
          aria-label={STRING_CONSTANTS.BACK}
          onClick={() => navigate(ROUTE_PATHS.SCHEME)}
          sx={{ ml: -1 }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Typography component="h1" sx={{ fontSize: 18, fontWeight: 700 }}>
          {`${STRING_CONSTANTS.JOIN} ${planName}`}
        </Typography>
      </Stack>

      {!master ? (
        <IfEmpty
          Icon={NoScheme}
          head={STRING_CONSTANTS.EMPTY}
          desc={STRING_CONSTANTS.NO_SCHEMES_TO_JOIN}
        />
      ) : (
        <>
          <Box
            sx={{
              borderRadius: schemeTokens.radius.card,
              border: "1px solid",
              borderColor: schemeTokens.color.border,
              bgcolor: schemeTokens.color.surface,
              px: 2,
              py: 2,
            }}
          >
            <Typography sx={schemeTokens.text.sectionTitle}>
              {STRING_CONSTANTS.CHOOSE_MONTHLY_AMOUNT}
            </Typography>
            <Typography sx={{ ...schemeTokens.text.hint, mt: 0.5, mb: 1.75 }}>
              {`${STRING_CONSTANTS.CHOOSE_AMOUNT_HELP} ${customerInstalments} months`}
            </Typography>

            <AmountPresetGrid
              presets={AMOUNT_PRESETS}
              value={toAmount(amount)}
              custom={custom}
              maturityOf={(value) => getMaturityValue(value, master)}
              onSelect={(value) => {
                setCustom(false)
                setAmount(value)
              }}
              onSelectCustom={() => setCustom(true)}
            />

            {custom && (
              <TextField
                fullWidth
                size="small"
                type="number"
                autoFocus
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
                placeholder={`${STRING_CONSTANTS.MIN} ${formatAmount(
                  master.minAmount
                )}`}
                error={!!amountError}
                helperText={
                  amountError ||
                  `${STRING_CONSTANTS.MULTIPLES_OF} ${formatAmount(
                    master.amountMultipleOf
                  )}`
                }
                sx={{ mt: 1.5 }}
              />
            )}
          </Box>

          <Box
            sx={{
              borderRadius: schemeTokens.radius.card,
              bgcolor: schemeTokens.color.stripBg,
              px: 2,
              py: 1.5,
            }}
          >
            <SummaryRow
              label={STRING_CONSTANTS.MONTHLY_INSTALMENT}
              value={formatAmount(selectedAmount)}
              highlight
            />
            <SummaryRow
              label={STRING_CONSTANTS.DURATION}
              value={`${customerInstalments + bonusInstalments} months`}
            />
            <SummaryRow
              label={`${STRING_CONSTANTS.YOU_PAY} (${customerInstalments} months)`}
              value={formatAmount(youPay)}
            />
            <Divider sx={{ my: 0.5 }} />
            <SummaryRow
              label={`${planName} ${STRING_CONSTANTS.CONTRIBUTES} (M${
                customerInstalments + bonusInstalments
              })`}
              value={formatAmount(contribution)}
              highlight
            />
            <Divider sx={{ my: 0.5 }} />
            <SummaryRow
              label={STRING_CONSTANTS.TOTAL_WALLET_VALUE}
              value={formatAmount(totalWalletValue)}
              highlight
              bold
            />
          </Box>

          <Box>
            <Typography sx={schemeTokens.text.sectionTitle}>
              {STRING_CONSTANTS.SELECT_STORE}
            </Typography>
            <Typography sx={{ ...schemeTokens.text.hint, mt: 0.5, mb: 1.25 }}>
              {STRING_CONSTANTS.STORE_HELP}
            </Typography>
            {storesFailed ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 13, color: "error.main" }}>
                  {STRING_CONSTANTS.STORE_OPTIONS_LOAD_FAILED}
                </Typography>
                <Button size="small" onClick={loadStores}>
                  {STRING_CONSTANTS.RETRY}
                </Button>
              </Stack>
            ) : (
              <TextField
                select
                fullWidth
                size="small"
                value={storeId ?? ""}
                onChange={(event) => setStoreId(event.target.value)}
                helperText={
                  stores.length === 0 ? STRING_CONSTANTS.NO_STORES_AVAILABLE : ""
                }
                inputProps={{ "aria-label": STRING_CONSTANTS.SELECT_STORE }}
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {toStoreLabel(store)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <Checkbox
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              size="small"
              sx={{ mt: -0.5 }}
              inputProps={{ "aria-label": STRING_CONSTANTS.AGREE_TERMS }}
            />
            <Typography sx={{ fontSize: 12.5 }}>
              {STRING_CONSTANTS.I_AGREE_TO_THE}{" "}
              <Box
                component={Link}
                to={ROUTE_PATHS.TERMS_AND_CONDITIONS}
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                {STRING_CONSTANTS.TERMS_AND_CONDITIONS}
              </Box>{" "}
              {`of the ${planName}.`}
            </Typography>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!canJoin({ agreed, amountError, submitting, storeId })}
            onClick={onSubmit}
            sx={{ fontWeight: 700, borderRadius: schemeTokens.radius.control }}
          >
            {STRING_CONSTANTS.JOIN_SCHEME}
          </Button>
        </>
      )}
    </SchemeContainer>
  )
}

export default JoinScheme
